import { useEffect, useRef, useState } from "react";
import { api, type SessionSummary } from "../api.js";
import { canShareFile, renderSummaryCard } from "../summaryCard.js";

/**
 * Sharing the session summary — FR-STAT-12, FR-NOTIF-4, FR-NOTIF-5.
 *
 * FR-NOTIF-5 dictates the structure of this component, in order:
 *
 *   1. **Render first, share second.** The image and the text are on screen
 *      before any button is pressed. If every browser API is missing the
 *      feature still works — the member long-presses the image to save it, or
 *      selects the text.
 *   2. **APIs are an accelerator, not the mechanism.** Native share and
 *      clipboard are attempted on tap and improve things when available; their
 *      absence changes nothing about whether the content can be obtained.
 *   3. **State the environment honestly.** A button that cannot work is not
 *      shown pretending it can.
 *
 * The reason for that insistence: Web Share and Clipboard need a secure
 * context and are simply undefined on `file://`, and fail on some mobile
 * browsers even over HTTPS. File sharing specifically is refused by more
 * browsers than plain-text sharing — `navigator.share` existing says nothing
 * about whether it will accept a file, which is why `canShareFile` tests with
 * the real file rather than sniffing for the API.
 *
 * The image is the headline because WhatsApp renders it as a card, and the
 * club asked for that. The text stays because an image cannot be searched in a
 * group chat six months later, and because it is the thing that still works
 * when everything else refuses.
 *
 * FR-NOTIF-4 settles the rest: there is no way to post to a WhatsApp group
 * programmatically, so the app's job ends at producing something worth
 * pasting.
 */
export function ShareSummary({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}): React.JSX.Element {
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [card, setCard] = useState<{ url: string; file: File } | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api
      .summary(sessionId)
      .then(setSummary)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not build the summary."),
      );
  }, [sessionId]);

  /**
   * Drawn as soon as the summary lands, not on tap — step 1 above. A share
   * button that spends two seconds rendering after being pressed reads as
   * broken, and on a court nobody waits to find out.
   */
  useEffect(() => {
    if (!summary) return;
    let live = true;

    renderSummaryCard(summary)
      .then(({ blob, url }) => {
        if (!live) return;
        const name = `core-baddy-${summary.date}.png`;
        setCard({ url, file: new File([blob], name, { type: "image/png" }) });
        setCardError(null);
      })
      .catch((e: unknown) => {
        // The text below is unaffected, so this is a note rather than an error.
        if (live) {
          setCardError(e instanceof Error ? e.message : "The image could not be drawn.");
        }
      });

    // Nothing to revoke: the URL is a data URL, which the garbage collector
    // handles. An object URL would need revoking here, and revoking it too
    // early is its own way of ending up with a blank image.
    return () => {
      live = false;
    };
  }, [summary]);

  // Checked at render, so a button is never offered that cannot work.
  const canShareText = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const canCopy =
    typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
  const canShareImage = card ? canShareFile(card.file) : false;

  if (error) {
    return (
      <main className="session">
        <p className="error">{error}</p>
        <button className="link" onClick={onClose}>
          Back
        </button>
      </main>
    );
  }

  if (!summary) return <main className="centre">Building the summary…</main>;

  return (
    <main className="session">
      <header className="session-head">
        <div className="brand">
          <h1 className="display">Share the session</h1>
          <div className="meta">The image pastes as a card; the text is searchable</div>
        </div>
        <button className="link" onClick={onClose}>
          Back
        </button>
      </header>

      {summary.provisional ? (
        <p className="sync-warning">
          These numbers are provisional — the session has not been committed, so
          they can still change. The image says so too.
        </p>
      ) : null}

      {/* Step 1 — the card, on screen, before anything is pressed. Long-pressing
          it is a complete way to use this feature with no API at all. */}
      {card ? (
        <>
          <img className="share-card" src={card.url} alt="Session summary" />
          <div className="row">
            {canShareImage ? (
              <button
                className="primary"
                onClick={() => {
                  navigator
                    .share({ files: [card.file], title: "Core Baddy" })
                    .then(() => setNote("Shared."))
                    .catch(() => setNote("Sharing was cancelled — the image above still works."));
                }}
              >
                Share the image
              </button>
            ) : null}

            <a className="chip" href={card.url} download={card.file.name}>
              Save image
            </a>
          </div>
          {!canShareImage ? (
            <p className="muted small">
              This browser will not hand a file to the share sheet. Save the
              image, or long-press it, then attach it in the chat.
            </p>
          ) : null}
        </>
      ) : cardError ? (
        <p className="muted small">{cardError} The text below is unaffected.</p>
      ) : (
        <p className="muted small">Drawing the image…</p>
      )}

      <h2>As text</h2>
      <textarea
        ref={textRef}
        className="share-text"
        readOnly
        rows={Math.min(24, summary.text.split("\n").length + 1)}
        value={summary.text}
        onFocus={(e) => e.currentTarget.select()}
        aria-label="Session summary, ready to copy"
      />

      <div className="row">
        {canCopy ? (
          <button
            className="chip"
            onClick={() => {
              navigator.clipboard
                .writeText(summary.text)
                .then(() => setNote("Copied."))
                .catch(() => setNote("Copying was blocked — select the text above instead."));
            }}
          >
            Copy text
          </button>
        ) : null}

        {canShareText ? (
          <button
            className="chip"
            onClick={() => {
              navigator
                .share({ text: summary.text, title: "Core Baddy" })
                .then(() => setNote("Shared."))
                .catch(() => setNote("Sharing was cancelled — the text above still works."));
            }}
          >
            Share text
          </button>
        ) : null}

        <button
          className="chip"
          onClick={() => {
            textRef.current?.focus();
            textRef.current?.select();
          }}
        >
          Select all
        </button>
      </div>

      {/* Step 3 — say so, rather than leaving a member wondering why there is
          no share button. */}
      {!canShareText && !canCopy ? (
        <p className="muted small">
          This browser does not offer sharing or clipboard access, so the image
          and the text above are the way to do it. Nothing is missing.
        </p>
      ) : null}

      {note ? <p className="muted small">{note}</p> : null}
    </main>
  );
}
